/**
 * FirmAssetsLibrary — biblioteka assetów firmowych per agency_client.
 *
 * Model B core: te assety są conditioning input dla AI generacji autopilota
 * (HeyGen avatar swap, fal style transfer, voice clone). Bez tych assetów AI
 * generuje "z powietrza" — z nimi tworzy warianty na bazie firmowych materiałów.
 */
import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Upload,
  Trash2,
  Image as ImageIcon,
  Video,
  Mic,
  Sparkles,
  Globe,
  X,
  Check,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { useToast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import {
  useFirmAssets,
  useCreateFirmAsset,
  useDeleteFirmAsset,
  useToggleAssetForClient,
  usePresignUpload,
  useScrapeWebsite,
  useImportFromScrape,
  type ScrapedBrand,
} from "@/hooks/useFirmAssets";
import { useAllUserMedia, CATEGORY_LABELS, SOURCE_LABELS } from "@/hooks/useUserMedia";
import { AutopilotPageShell } from "@/components/autopilot/AutopilotPageHeader";
import type { FirmCreativeAsset } from "@shared/schema";

const ASSET_TYPE_OPTIONS: Array<{
  value: string;
  label: string;
  mediaType: "image" | "video" | "audio";
}> = [
  { value: "brand_logo", label: "Logo marki", mediaType: "image" },
  { value: "product_photo", label: "Zdjęcie produktu", mediaType: "image" },
  { value: "avatar_face", label: "Twarz / avatar (statyczna)", mediaType: "image" },
  { value: "founder_video", label: "Video właściciela / CEO", mediaType: "video" },
  { value: "testimonial", label: "Testimonial klienta", mediaType: "video" },
  { value: "voice_sample", label: "Próbka głosu (voice clone)", mediaType: "audio" },
  { value: "style_reference", label: "Style reference (mood)", mediaType: "image" },
  {
    value: "brand_palette",
    label: "Paleta kolorów + typografia (screenshot/font specimen)",
    mediaType: "image",
  },
  { value: "b_roll", label: "B-roll (do montażu)", mediaType: "video" },
  { value: "jingle", label: "Jingiel / muzyka", mediaType: "audio" },
  { value: "icon_pack", label: "Ikony / pack", mediaType: "image" },
];

function mapCategoryToAssetType(
  category: "logo" | "face" | "product" | "scene" | "video" | "audio" | "other",
  mediaType: "image" | "video" | "audio"
): string {
  if (category === "face") return "avatar_face";
  if (category === "logo") return "brand_logo";
  if (category === "product") return "product_photo";
  if (category === "scene") return "style_reference";
  if (category === "video") return "founder_video";
  if (category === "audio") return "voice_sample";
  if (mediaType === "video") return "b_roll";
  if (mediaType === "audio") return "jingle";
  return "style_reference";
}

const TYPE_ICON: Record<string, typeof ImageIcon> = {
  brand_logo: Sparkles,
  product_photo: ImageIcon,
  style_reference: ImageIcon,
  icon_pack: ImageIcon,
  founder_video: Video,
  testimonial: Video,
  b_roll: Video,
  voice_sample: Mic,
  jingle: Mic,
};

export default function FirmAssetsLibrary() {
  const selectedClientId = useStore((s) => s.selectedClientId);
  const selectedClientName = useStore((s) => s.selectedClientName);
  const scopeKey = selectedClientId ?? null;
  const { data, isLoading } = useFirmAssets(scopeKey);
  const items = data?.items ?? [];
  // Unified — pokazujemy też user_avatars, user_heygen_avatars, user_media_assets (oprócz firm_creative_assets).
  // User chce widzieć WSZYSTKO co kiedykolwiek wgrał w jednej bibliotece.
  const { data: allMediaData } = useAllUserMedia();
  const allMedia = allMediaData?.items ?? [];
  const { toast } = useToast();

  const [uploading, setUploading] = useState(false);
  const [assetType, setAssetType] = useState<string>("brand_logo");
  const [title, setTitle] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const presignMut = usePresignUpload();
  const createMut = useCreateFirmAsset();
  const deleteMut = useDeleteFirmAsset(scopeKey);
  const toggleClientMut = useToggleAssetForClient(scopeKey);

  // Website scrape state
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraped, setScraped] = useState<ScrapedBrand | null>(null);
  const [selectedLogos, setSelectedLogos] = useState<Set<string>>(new Set());
  const [selectedColors, setSelectedColors] = useState<Set<string>>(new Set());
  const [selectedFonts, setSelectedFonts] = useState<Set<string>>(new Set());
  const [includeHero, setIncludeHero] = useState(true);
  const scrapeMut = useScrapeWebsite();
  const importMut = useImportFromScrape();

  const handleScrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraped(null);
    try {
      const result = await scrapeMut.mutateAsync({
        url: scrapeUrl.trim(),
        agencyClientId: scopeKey,
      });
      setScraped(result);
      // Domyślnie zaznacz pierwsze logo (najlepszy kandydat — apple-touch-icon HD) + wszystkie kolory
      setSelectedLogos(new Set(result.logoCandidates.slice(0, 1).map((l) => l.url)));
      setSelectedColors(new Set(result.colors));
      setSelectedFonts(new Set(result.fonts ?? []));
      setIncludeHero(!!result.heroImage);
    } catch (err) {
      toast({
        title: "Nie udało się pobrać strony",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!scraped) return;
    try {
      const logos = scraped.logoCandidates.filter((l) => selectedLogos.has(l.url));
      const colors = scraped.colors.filter((c) => selectedColors.has(c));
      const fonts = (scraped.fonts ?? []).filter((f) => selectedFonts.has(f));
      const heroImage = includeHero ? scraped.heroImage : null;
      const result = await importMut.mutateAsync({
        agencyClientId: scopeKey,
        sourceUrl: scraped.url,
        logos: logos.map((l) => ({ url: l.url, source: l.source })),
        colors,
        fonts,
        heroImage,
        siteName: scraped.siteName,
      });
      toast({
        title: "Zaimportowano do biblioteki",
        description: `${result.created} assetów dodanych`,
      });
      setScrapeOpen(false);
      setScraped(null);
      setScrapeUrl("");
    } catch (err) {
      toast({
        title: "Nie udało się zaimportować",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  const toggleLogo = (url: string) => {
    setSelectedLogos((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };
  const toggleColor = (color: string) => {
    setSelectedColors((prev) => {
      const next = new Set(prev);
      if (next.has(color)) next.delete(color);
      else next.add(color);
      return next;
    });
  };
  const toggleFont = (font: string) => {
    setSelectedFonts((prev) => {
      const next = new Set(prev);
      if (next.has(font)) next.delete(font);
      else next.add(font);
      return next;
    });
  };

  const selectedTypeMeta = ASSET_TYPE_OPTIONS.find((o) => o.value === assetType)!;

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast({ title: "Wybierz plik", variant: "destructive" });
      return;
    }
    if (!title.trim()) {
      toast({ title: "Podaj tytuł assetu", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, getUrl } = await presignMut.mutateAsync({
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
      });
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) throw new Error(`R2 upload failed: ${putRes.status}`);
      await createMut.mutateAsync({
        agencyClientId: scopeKey,
        assetType,
        mediaType: selectedTypeMeta.mediaType,
        title: title.trim(),
        fileUrl: getUrl,
        fileSizeBytes: file.size,
      });
      toast({ title: "Asset dodany", description: `${title} → biblioteka` });
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast({
        title: "Błąd uploadu",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <AutopilotPageShell
      title="Biblioteka assetów firmowych"
      subtitle={
        <>
          Klient: <Badge variant="secondary">{selectedClientName ?? "Globalna"}</Badge>
          {" · "}AI autopilota generuje warianty na bazie tych assetów (Model B).
        </>
      }
      actions={
        <Button variant="outline" onClick={() => setScrapeOpen((v) => !v)} className="gap-2">
          <Globe className="h-4 w-4" />
          {scrapeOpen ? "Zamknij" : "Importuj ze strony www"}
        </Button>
      }
    >
      {scrapeOpen && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4" /> Importuj branding ze strony klienta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <div>
                <Label>URL strony klienta</Label>
                <Input
                  value={scrapeUrl}
                  onChange={(e) => setScrapeUrl(e.target.value)}
                  placeholder="https://stylowa-xl.pl"
                  type="url"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleScrape}
                  disabled={scrapeMut.isPending || !scrapeUrl.trim()}
                  className="gap-2"
                >
                  {scrapeMut.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                  Pobierz
                </Button>
              </div>
            </div>

            {scraped && (
              <div className="space-y-4 pt-3 border-t">
                <div className="text-sm">
                  <strong>{scraped.siteName ?? scraped.title ?? scraped.url}</strong>
                  {scraped.description && (
                    <p className="text-muted-foreground text-xs mt-1">{scraped.description}</p>
                  )}
                </div>

                {scraped.logoCandidates.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Kandydaci na logo
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                      {scraped.logoCandidates.map((logo) => {
                        const isSelected = selectedLogos.has(logo.url);
                        return (
                          <button
                            key={logo.url}
                            type="button"
                            onClick={() => toggleLogo(logo.url)}
                            className={`relative rounded border-2 p-2 transition ${isSelected ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
                          >
                            <img
                              src={logo.url}
                              alt="logo"
                              className="w-full h-20 object-contain bg-white/5 rounded"
                            />
                            <div className="text-[10px] text-muted-foreground mt-1 truncate">
                              {logo.source}
                              {logo.size ? ` · ${logo.size}` : ""}
                            </div>
                            {isSelected && (
                              <div className="absolute top-1 right-1 bg-primary rounded-full p-0.5">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {scraped.colors.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Kolory motywu
                    </Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {scraped.colors.map((color) => {
                        const isSelected = selectedColors.has(color);
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => toggleColor(color)}
                            className={`flex items-center gap-2 px-2 py-1 rounded border-2 transition ${isSelected ? "border-primary" : "border-white/10 hover:border-white/30"}`}
                          >
                            <span className="w-5 h-5 rounded" style={{ backgroundColor: color }} />
                            <span className="text-xs font-mono">{color}</span>
                            {isSelected && <Check className="h-3 w-3 text-primary" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(scraped.fonts ?? []).length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Czcionki brandu
                    </Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(scraped.fonts ?? []).map((font) => {
                        const isSelected = selectedFonts.has(font);
                        return (
                          <button
                            key={font}
                            type="button"
                            onClick={() => toggleFont(font)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded border-2 transition ${isSelected ? "border-primary" : "border-white/10 hover:border-white/30"}`}
                          >
                            <span
                              className="text-sm"
                              style={{ fontFamily: `"${font}", sans-serif` }}
                            >
                              Aa
                            </span>
                            <span className="text-xs">{font}</span>
                            {isSelected && <Check className="h-3 w-3 text-primary" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {scraped.heroImage && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Hero / cover image
                    </Label>
                    <button
                      type="button"
                      onClick={() => setIncludeHero((v) => !v)}
                      className={`mt-2 block w-full rounded border-2 p-2 transition ${includeHero ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
                    >
                      <img
                        src={scraped.heroImage}
                        alt="hero"
                        className="w-full max-h-40 object-cover rounded"
                      />
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {includeHero ? "Zostanie zapisany" : "Pominięty"}
                      </div>
                    </button>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setScraped(null);
                      setScrapeUrl("");
                    }}
                  >
                    <X className="h-4 w-4 mr-1" /> Anuluj
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={
                      importMut.isPending ||
                      (selectedLogos.size === 0 && selectedColors.size === 0 && !includeHero)
                    }
                    className="gap-2"
                  >
                    {importMut.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    Zapisz do biblioteki
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4" /> Dodaj asset
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label>Typ</Label>
              <Select value={assetType} onValueChange={setAssetType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Tytuł</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="np. Founder video — pitch 30s"
              />
            </div>
          </div>
          <div>
            <Label>Plik</Label>
            <Input
              ref={fileRef}
              type="file"
              accept={
                selectedTypeMeta.mediaType === "image"
                  ? "image/*"
                  : selectedTypeMeta.mediaType === "video"
                    ? "video/*"
                    : "audio/*"
              }
            />
          </div>
          <Button onClick={handleUpload} disabled={uploading}>
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Wgraj
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Lista ({items.length})</span>
            {selectedClientId && (
              <span className="text-xs text-muted-foreground font-normal">
                Zaznacz które są aktywne dla <strong>{selectedClientName}</strong>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded border border-white/10 p-3 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Skeleton className="h-4 w-4 shrink-0" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                    <Skeleton className="h-8 w-8 shrink-0 rounded-md" />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-5 w-14 rounded-full" />
                  </div>
                  <Skeleton className="h-32 w-full rounded" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              Brak assetów. AI będzie generować z powietrza dopóki nie wgrasz.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {items.map((a: FirmCreativeAsset) => {
                const Icon = TYPE_ICON[a.assetType] ?? Sparkles;
                const meta = (a.metadata ?? {}) as Record<string, unknown>;
                const disabledFor = Array.isArray(meta.disabledForClients)
                  ? (meta.disabledForClients as string[])
                  : [];
                const activeForCurrentClient = selectedClientId
                  ? !disabledFor.includes(selectedClientId)
                  : true;
                return (
                  <div
                    key={a.id}
                    className={`rounded border p-3 flex flex-col gap-2 transition ${selectedClientId && !activeForCurrentClient ? "opacity-50 border-white/10" : "border-emerald-500/30"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <label
                        className="flex items-center gap-2 min-w-0 cursor-pointer"
                        title={
                          selectedClientId
                            ? `Aktywny dla ${selectedClientName}`
                            : "Wybierz klienta w sidebarze żeby zarządzać per-client"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={activeForCurrentClient}
                          disabled={!selectedClientId || toggleClientMut.isPending}
                          onChange={(e) => {
                            if (!selectedClientId) return;
                            toggleClientMut.mutate({
                              id: a.id,
                              clientId: selectedClientId,
                              enabled: e.target.checked,
                            });
                          }}
                          className="shrink-0 accent-emerald-500"
                        />
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="font-medium text-sm truncate">{a.title}</span>
                      </label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (confirm(`Usunąć "${a.title}" z biblioteki na zawsze?`))
                            deleteMut.mutate(a.id);
                        }}
                        disabled={deleteMut.isPending}
                        title="Usuń z biblioteki (permanent)"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline" className="text-xs">
                        {a.assetType}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {a.mediaType}
                      </Badge>
                      {selectedClientId && !activeForCurrentClient && (
                        <Badge
                          variant="outline"
                          className="text-[10px] text-amber-300 border-amber-500/30"
                        >
                          wyłączone dla {selectedClientName}
                        </Badge>
                      )}
                    </div>
                    {a.mediaType === "image" && a.fileUrl ? (
                      <img
                        src={a.fileUrl}
                        alt={a.title}
                        className="rounded max-h-32 object-cover"
                      />
                    ) : a.mediaType === "video" && a.fileUrl ? (
                      <video src={a.fileUrl} className="rounded max-h-32" controls />
                    ) : a.mediaType === "audio" && a.fileUrl ? (
                      <audio src={a.fileUrl} controls className="w-full" />
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Avatary + media z innych miejsc — checkbox aktywuje (auto-import do firm_assets + active per-klient) */}
      {(() => {
        const externalMedia = allMedia.filter((m) => m.source !== "firm_assets");
        if (externalMedia.length === 0) return null;
        const byFileUrl = new Map(items.map((a) => [a.fileUrl, a] as const));
        return (
          <Card className="border-blue-500/30 mt-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-blue-400" /> Avatary i media z innych miejsc (
                  {externalMedia.length})
                </span>
                {selectedClientId && (
                  <span className="text-xs text-muted-foreground font-normal">
                    Zaznacz które aktywować dla <strong>{selectedClientName}</strong>
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                Twoje avatary z UGC studio + HeyGen + uploady z innych modułów. Zaznacz checkbox
                żeby AI autopilota i kreatory używały tego konkretnego avatara.
              </p>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {externalMedia.map((m) => {
                  const linked = byFileUrl.get(m.fileUrl);
                  const linkedMeta = (linked?.metadata ?? {}) as Record<string, unknown>;
                  const linkedDisabledFor = Array.isArray(linkedMeta.disabledForClients)
                    ? (linkedMeta.disabledForClients as string[])
                    : [];
                  const isActive =
                    !!linked &&
                    (selectedClientId ? !linkedDisabledFor.includes(selectedClientId) : true);
                  const busy = createMut.isPending || toggleClientMut.isPending;
                  return (
                    <div
                      key={m.uniqueKey}
                      className={`rounded border p-3 flex flex-col gap-2 transition ${isActive ? "border-emerald-500/30" : "border-white/10 opacity-70"}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <label
                          className="flex items-center gap-2 min-w-0 cursor-pointer"
                          title={
                            selectedClientId
                              ? `Aktywuj dla ${selectedClientName}`
                              : "Wybierz klienta w sidebarze żeby zarządzać per-klient"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={isActive}
                            disabled={busy}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (!linked) {
                                if (!checked) return;
                                const assetType = mapCategoryToAssetType(m.category, m.mediaType);
                                createMut.mutate(
                                  {
                                    agencyClientId: scopeKey,
                                    assetType,
                                    mediaType: m.mediaType,
                                    title: m.title,
                                    description: m.description ?? null,
                                    fileUrl: m.fileUrl,
                                    thumbnailUrl: m.thumbnailUrl ?? null,
                                    metadata: {
                                      importedFromSource: m.source,
                                      importedFromId: m.id,
                                    },
                                  },
                                  {
                                    onSuccess: () =>
                                      toast({
                                        title: "Aktywowane",
                                        description: `"${m.title}" jest teraz używane${selectedClientId ? ` przez ${selectedClientName}` : ""}.`,
                                      }),
                                    onError: (err) =>
                                      toast({
                                        title: "Nie udało się aktywować",
                                        description: FriendlyErrorMapper.map(err).description,
                                        variant: "destructive",
                                      }),
                                  }
                                );
                                return;
                              }
                              if (selectedClientId) {
                                toggleClientMut.mutate({
                                  id: linked.id,
                                  clientId: selectedClientId,
                                  enabled: checked,
                                });
                              }
                            }}
                            className="shrink-0 accent-emerald-500"
                          />
                          <span className="font-medium text-sm truncate" title={m.title}>
                            {m.title}
                          </span>
                        </label>
                        <Badge variant="outline" className="text-[10px] shrink-0">
                          {SOURCE_LABELS[m.source]}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="secondary" className="text-xs">
                          {CATEGORY_LABELS[m.category]}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {m.mediaType}
                        </Badge>
                        {linked && selectedClientId && !isActive && (
                          <Badge
                            variant="outline"
                            className="text-[10px] text-amber-300 border-amber-500/30"
                          >
                            wyłączone dla {selectedClientName}
                          </Badge>
                        )}
                      </div>
                      {m.mediaType === "image" && m.fileUrl ? (
                        <img
                          src={m.thumbnailUrl ?? m.fileUrl}
                          alt={m.title}
                          className="rounded max-h-32 object-cover"
                          loading="lazy"
                        />
                      ) : m.mediaType === "video" && m.fileUrl ? (
                        <video src={m.fileUrl} className="rounded max-h-32" controls />
                      ) : null}
                      {m.description && (
                        <p className="text-[11px] text-muted-foreground line-clamp-2">
                          {m.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </AutopilotPageShell>
  );
}
